import { existsSync, readdirSync } from "node:fs";
import { mkdir, writeFile } from "node:fs/promises";
import { join } from "node:path";
import {
  generateIndexFile,
  generateJavaScriptComponent,
  generateTypeScriptComponent,
} from "../templates/component";
import type { Config } from "../types";
import { getComponentFilename } from "./naming";
import { getSvgPath, parseSvg } from "./svg-parser";

/**
 * Generate a single component file
 */
export async function generateComponent(
  iconName: string,
  svgDir: string,
  config: Config,
): Promise<string> {
  const svgPath = getSvgPath(svgDir, iconName);
  const iconData = await parseSvg(svgPath, iconName);

  // Generate component code
  const componentCode = config.typescript
    ? generateTypeScriptComponent(iconData)
    : generateJavaScriptComponent(iconData);

  // Determine output path
  const filename = getComponentFilename(iconName, config.extension);
  const outputPath = join(config.output, filename);

  // Ensure output directory exists
  if (!existsSync(config.output)) {
    await mkdir(config.output, { recursive: true });
  }

  // Write component file
  await writeFile(outputPath, componentCode, "utf-8");

  return outputPath;
}

/**
 * Generate components for multiple icons
 */
export async function generateComponents(
  iconNames: string[],
  svgDir: string,
  config: Config,
  onProgress?: (current: number, total: number, iconName: string) => void,
): Promise<string[]> {
  const generatedPaths: string[] = [];

  for (let i = 0; i < iconNames.length; i++) {
    const iconName = iconNames[i];
    if (!iconName) continue;

    if (onProgress) {
      onProgress(i + 1, iconNames.length, iconName);
    }

    try {
      const outputPath = await generateComponent(iconName, svgDir, config);
      generatedPaths.push(outputPath);
    } catch (error) {
      console.error(`Error generating ${iconName}:`, error);
      // Continue with other icons
    }
  }

  // Generate index file if requested
  if (config.indexFile && generatedPaths.length > 0) {
    const allFiles = existsSync(config.output)
      ? readdirSync(config.output)
      : [];

    const componentFiles = allFiles.filter((file) => {
      const isIndexFile = file.startsWith("index.");
      const lowerFile = file.toLowerCase();
      const hasValidExtension =
        lowerFile.endsWith(".tsx") ||
        lowerFile.endsWith(".ts") ||
        lowerFile.endsWith(".jsx") ||
        lowerFile.endsWith(".js");
      return !isIndexFile && hasValidExtension;
    });

    const componentNames = componentFiles
      .map((file) => file.replace(/\.(tsx|ts|jsx|js)$/i, ""))
      .filter((name, index, array) => array.indexOf(name) === index)
      .sort((a, b) => a.localeCompare(b));

    const indexContent = generateIndexFile(componentNames);
    const indexExt = config.typescript ? "ts" : "js";
    const indexPath = join(config.output, `index.${indexExt}`);

    await writeFile(indexPath, indexContent, "utf-8");
    generatedPaths.push(indexPath);
  }

  return generatedPaths;
}

/**
 * Check if component file already exists
 */
export async function componentExists(
  iconName: string,
  config: Config,
): Promise<boolean> {
  const filename = getComponentFilename(iconName, config.extension);
  const outputPath = join(config.output, filename);
  return existsSync(outputPath);
}
